import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { StoreApi } from "zustand";
import type { AuthSlice } from "../auth/auth_store";
import {
  buildInitialState,
  type GameActions,
  type GameState,
} from "./game/state";

/**
 * The unified application store.
 *
 * The auth/session lifecycle and the table/hand lifecycle were two
 * separate `create()` Zustand instances. This module collapses them
 * into ONE store composed from two slices — so the historic two-store
 * drift class (a mirror copying `mySeat` / `mode` between two
 * independent stores that could observe each other mid-update) becomes
 * physically impossible: both slices now live behind a single atomic
 * root store.
 *
 * The two slices are held under SEPARATE sub-keys (`auth` / `game`)
 * rather than flattened, because they legitimately share field names
 * with DIFFERENT meanings + types (most notably `phase`:
 * `AuthPhase = "logged_out" | "lobby" | "table"` vs
 * `Phase = "lobby" | "hand_active" | …`). A flat `AuthSlice & GameSlice`
 * would intersect those keys (collapsing `phase` to the single shared
 * literal `"lobby"`) and collide at runtime. Sub-keying keeps each
 * slice's surface intact while still sharing ONE store + ONE atomic
 * commit. Each slice action receives a SCOPED `set` / `get` bound to
 * its own sub-state, so its action bodies stay byte-identical (they
 * still see + return their own slice shape).
 *
 * GAME-SLICE LAZY MOUNT (preserves the de-eager invariant):
 * `root.ts` is in the EAGER cold-login graph (`App.tsx` →
 * `auth_store.ts` → here). It must NOT statically import
 * `store/game/store.ts`, whose `WS_EVENT_REDUCERS` table drags the full
 * hand_lifecycle / seed / timer reducer graph (~18 KB gz) — that would
 * land the reducers in the entry chunk and break the
 * `app_degear_chunk` guard. So root eagerly imports only the
 * REDUCER-FREE `buildInitialState` (from `state.ts`) to seed the game
 * sub-state, and the game ACTION methods (the reducer graph) are
 * registered LATER by `store/game/store.ts` when the lazy Game chunk
 * first loads (it self-registers via `mountGameSlice` on module eval).
 * Every game consumer reaches the store through `useGameStore` — which
 * is exported by `store/game/store.ts` — so importing the hook is what
 * loads + mounts the actions; they are always present before any game
 * action is invoked.
 *
 * Import-graph invariant (asserted by
 * `tests/unit/store_root_no_ws.test.ts`): `root.ts` MUST NOT import
 * `ws/client` (directly or transitively), and (the de-eager corollary)
 * MUST NOT import `store/game/store.ts` or `store/game/reducers/*`. It
 * imports ONLY the reducer-free game state seed; both slices register
 * themselves (`registerAuthSlice`, `mountGameSlice`), so nothing it
 * imports imports it back. `ws/client` reaches the store at runtime via `getState()`,
 * never through a static edge into this module.
 */
export interface RootState {
  auth: AuthSlice;
  game: GameState & GameActions;
}

type GameSlice = RootState["game"];

/** A Zustand-shaped `setState` scoped to a single slice `S` — the
 *  same `(partial | updater, replace?)` surface the slice factories'
 *  action bodies already use, but writing into the slice's own
 *  sub-state instead of the whole store. */
export type SliceSet<S> = {
  (partial: S | Partial<S> | ((state: S) => S | Partial<S>)): void;
  (state: S | ((state: S) => S), replace: true): void;
};

/**
 * Build a `setState` scoped to one root sub-key (`auth` / `game`),
 * replicating Zustand's OWN standalone-store `setState` semantics
 * exactly so each slice action behaves byte-identically:
 *
 *   1. Resolve the partial / updater against the CURRENT sub-state.
 *   2. SAME-VALUE BAILOUT — if the action returns the identical
 *      sub-state object (`Object.is`), do NOT write. This preserves
 *      the reducer-level identity bailouts (e.g.
 *      `stampSeedLocalError`'s same-value short-circuit) on which the
 *      render-loop guards depend: the sub-slice reference stays stable
 *      and no subscriber re-fires — exactly as a standalone store
 *      returning `state` unchanged.
 *   3. Otherwise shallow-merge the partial into the sub-state
 *      (`Object.assign({}, sub, next)`), matching Zustand's default
 *      (non-replace) merge, and commit it under the sub-key. The
 *      `replace: true` overload replaces the sub-state wholesale (kept
 *      for surface parity; no slice action uses it today).
 */
function makeSliceSet<K extends keyof RootState>(
  set: StoreApi<RootState>["setState"],
  get: StoreApi<RootState>["getState"],
  key: K,
): SliceSet<RootState[K]> {
  type S = RootState[K];
  return ((partial: unknown, replace?: boolean): void => {
    const sub = get()[key];
    const next =
      typeof partial === "function"
        ? (partial as (s: S) => S | Partial<S>)(sub)
        : (partial as S | Partial<S>);
    if (Object.is(next, sub)) return;
    const merged = (
      replace === true ? (next as S) : Object.assign({}, sub, next)
    ) as S;
    set({ [key]: merged } as Partial<RootState>);
  }) as SliceSet<S>;
}

/** Factory for the game ACTION methods (the reducer graph). Supplied by
 *  `store/game/store.ts` via `mountGameSlice` when the lazy Game chunk
 *  loads. `null` until then — the game sub-state seeds from the
 *  reducer-free `buildInitialState`, which carries no actions. */
let gameActionsFactory:
  | ((set: SliceSet<GameSlice>, get: () => GameSlice) => GameActions)
  | null = null;

/** Factory for the auth slice. Supplied by `auth/auth_store.ts` via
 *  `registerAuthSlice` when that module evaluates.
 *
 *  Registered rather than imported: `auth_store` needs this module for
 *  `useRootStore` and the table drain, so importing its factory here
 *  made the two modules import each other. The cycle was survivable
 *  (the store is created on first access, after both evaluate) but it
 *  was a cycle, and the build said so on every run. */
let authSliceFactory:
  | ((set: SliceSet<AuthSlice>, get: () => AuthSlice) => AuthSlice)
  | null = null;

let singleton: StoreApi<RootState> | null = null;

/** Supply the auth slice. Called once by `auth/auth_store.ts` on module
 *  eval — before any code can reach the store, since every path to it
 *  goes through `auth_store` or imports it first (`store/game/store`). */
export function registerAuthSlice(
  factory: (set: SliceSet<AuthSlice>, get: () => AuthSlice) => AuthSlice,
): void {
  authSliceFactory = factory;
}

/** Compose the game ACTION methods into the live game sub-slice (or
 *  arm them for the next store creation). Called once by
 *  `store/game/store.ts` on module eval. Idempotent: a second call
 *  re-arms the factory + re-mounts (harmless — the actions are pure
 *  references). When the store already exists (auth path created it
 *  first, before any game consumer loaded the Game chunk), this mounts
 *  the actions into the existing sub-state via a scoped `set` so the
 *  game view immediately resolves its methods. */
export function mountGameSlice(
  factory: (set: SliceSet<GameSlice>, get: () => GameSlice) => GameActions,
): void {
  gameActionsFactory = factory;
  if (singleton !== null) {
    const set = makeSliceSet(singleton.setState, singleton.getState, "game");
    const get = (): GameSlice => singleton!.getState().game;
    set(factory(set, get));
  }
}

function buildRootState(
  set: StoreApi<RootState>["setState"],
  get: StoreApi<RootState>["getState"],
): RootState {
  const authSet = makeSliceSet(set, get, "auth");
  const authGet = (): AuthSlice => get().auth;

  const gameSet = makeSliceSet(set, get, "game");
  const gameGet = (): GameSlice => get().game;

  // Seed the game sub-state from the REDUCER-FREE initial state. The
  // action methods attach via `mountGameSlice` when the Game chunk
  // loads; if it has already loaded (the common path: a game consumer
  // imported `useGameStore` first), compose them now.
  const gameBase = buildInitialState() as GameSlice;
  const gameActions =
    gameActionsFactory !== null ? gameActionsFactory(gameSet, gameGet) : null;

  if (authSliceFactory === null) {
    // A store without its auth slice would not fail here — it would
    // fail later, somewhere that reads `root.auth`, with nothing
    // pointing back at the cause.
    throw new Error(
      "root store created before the auth slice registered — import auth/auth_store first",
    );
  }
  return {
    auth: authSliceFactory(authSet, authGet),
    game: gameActions !== null ? { ...gameBase, ...gameActions } : gameBase,
  };
}

/**
 * Lazy singleton. `create()` is deferred to first ACCESS (not module-
 * eval) so the slices have registered themselves by the time the
 * store is composed — `auth_store` registers on its own evaluation,
 * which precedes any access. The store is created exactly once and
 * memoised in `singleton`.
 */
export function rootStore(): StoreApi<RootState> {
  if (singleton === null) {
    singleton = createStore<RootState>()((set, get) =>
      buildRootState(set, get),
    );
  }
  return singleton;
}

/**
 * Wipe the game/table sub-slice back to its reducer-free initial state
 * (the table-teardown drain).
 *
 * This is the single canonical felt drain. The game-slice `reset()`
 * action (lazy Game chunk) delegates here, and — crucially — the AUTH
 * transitions that tear a table down (`leaveTable`, the logout/revoke
 * sequence) call it INSIDE their own atomic root `set` so the auth-lane
 * flip and the felt wipe commit together. Driving the teardown off the
 * auth TRANSITION (not an effect riding the mounted Game component)
 * means logout-from-lobby drains the felt even with `<Game />`
 * unmounted, and removes the absorbing-state ordering race the
 * effect-paired `reset()` call-sites carried.
 *
 * Two forms:
 *   - `resetTableSlice(game)` — PURE: returns the wiped game sub-state
 *     merged over the live one (actions preserved, every state field
 *     reset to `buildInitialState()`), for an auth transition that
 *     wants the wipe in the SAME root `set` as its auth-lane flip.
 *   - `useRootStore.resetTableSlice()` — IMPERATIVE: runs the wipe as
 *     its own atomic root `set`. Kept for any standalone caller; the
 *     auth transitions use the pure form so the wipe rides their
 *     existing single `set`.
 *
 * Neither form touches the UI notice latches — a felt drain / reset
 * is a TEARDOWN, and the session-revoke / surface-displaced notices
 * must OUTLIVE the teardown that produced them (the prior one-render
 * flash root). The latches clear only on the modal CTA / a fresh
 * `login()` (`clearPendingNotices`).
 */
export function resetTableSlice(game: GameSlice): GameSlice {
  // Shallow-merge the fresh STATE over the live slice so the game
  // ACTION methods (mounted via `mountGameSlice`) survive the wipe —
  // exactly what the scoped `makeSliceSet` merge does for the game
  // slice's own `reset()` (`set(buildInitialState())`).
  return { ...game, ...buildInitialState() };
}

/**
 * `useRootStore` — the React hook + Zustand store-API surface, both
 * delegating to the lazily-created singleton. Callable with a selector
 * (`useRootStore((s) => …)`) and carries `getState` / `setState` /
 * `subscribe` / `getInitialState` for the views to delegate to, plus
 * `resetTableSlice` (the canonical felt drain — see the helper doc).
 */
function useRootStoreHook<T>(selector: (state: RootState) => T): T {
  return useStore(rootStore(), selector);
}

export const useRootStore: typeof useRootStoreHook &
  Pick<
    StoreApi<RootState>,
    "getState" | "setState" | "subscribe" | "getInitialState"
  > & { resetTableSlice(): void } = Object.assign(useRootStoreHook, {
  getState: () => rootStore().getState(),
  setState: ((...args: Parameters<StoreApi<RootState>["setState"]>) =>
    rootStore().setState(...args)) as StoreApi<RootState>["setState"],
  subscribe: (listener: Parameters<StoreApi<RootState>["subscribe"]>[0]) =>
    rootStore().subscribe(listener),
  getInitialState: () => rootStore().getInitialState(),
  resetTableSlice: (): void => {
    rootStore().setState((root) => ({ game: resetTableSlice(root.game) }));
  },
});
